// src/modules/auth.js — Authentication, session verification, sign-out, waitlist, PHI retention
import { state } from '../state.js';
import { API_BASE, sendBeacon,
    USER_META_KEY, LOCAL_PHI_RETENTION_DAYS, LOCAL_PHI_RETENTION_MS,
    UNSENT_KEPT_KEY, UNSENT_SESSION_STATUSES,
} from '../utils/constants.js';
import { getStoredSessionTokenOnly,
    setSessionToken, clearSessionToken, apiFetch,
} from '../utils/api.js';
import { showView, setStatus } from '../utils/dom.js';
import { logger } from './debug.js';
import { RecordingStore, SessionStore, AudioCache } from '../utils/storage.js';
import { ProfileStore } from '../stores/profile-store.js';

// ============================================================================
// LOCAL PHI RETENTION
// ============================================================================

function sessionTimestampMs(session) {
    const raw = session.lastSavedAt || session.startedAt;
    const ts = raw ? new Date(raw).getTime() : NaN;
    return Number.isFinite(ts) ? ts : Date.now();
}

export async function clearLocalPhiOnSignOut(migrateLegacyRecordingsFn) {
    let keptUnsent = 0;
    try {
        const sessions = await SessionStore.getAll();
        for (const session of sessions) {
            if (UNSENT_SESSION_STATUSES.indexOf(session.status) >= 0) {
                keptUnsent++;
                continue;
            }
            try { await SessionStore.remove(session.id); } catch (e) {
                logger.warn('SYSTEM', 'Sign-out cleanup could not remove session', { id: session.id, error: e.message });
            }
        }
    } catch (err) {
        logger.warn('SYSTEM', 'Sign-out session cleanup failed', { error: err.message });
    }

    try {
        if (migrateLegacyRecordingsFn) await migrateLegacyRecordingsFn();
        const remaining = await RecordingStore.getAll();
        if (remaining.length === 0) {
            await RecordingStore.clear();
        } else {
            keptUnsent += remaining.length;
            logger.warn('SYSTEM', 'Legacy pending uploads kept on sign-out (unsent audio)', { count: remaining.length });
        }
    } catch (err) {
        logger.warn('SYSTEM', 'Legacy store cleanup failed', { error: err.message });
    }

    try {
        if (keptUnsent > 0) localStorage.setItem(UNSENT_KEPT_KEY, String(keptUnsent));
        else localStorage.removeItem(UNSENT_KEPT_KEY);
    } catch (e) { /* storage disabled */ }

    AudioCache.clear();
    logger.info('SYSTEM', 'Local PHI cleared on sign-out', { keptUnsent, retentionDays: LOCAL_PHI_RETENTION_DAYS });
}

export async function reapExpiredLocalData() {
    const cutoff = Date.now() - LOCAL_PHI_RETENTION_MS;
    let reapedSessions = 0;
    try {
        const sessions = await SessionStore.getAll();
        for (const session of sessions) {
            if (sessionTimestampMs(session) < cutoff) {
                try { await SessionStore.remove(session.id); reapedSessions++; } catch (e) { /* keep going */ }
            }
        }
    } catch (err) {
        logger.warn('SYSTEM', 'Session reaper failed', { error: err.message });
    }

    let reapedLegacy = 0;
    try {
        const legacy = await RecordingStore.getAll();
        const expired = legacy.filter(entry => {
            const ts = entry.timestamp ? new Date(entry.timestamp).getTime() : NaN;
            return Number.isFinite(ts) ? ts < cutoff : false;
        });
        if (legacy.length > 0 && expired.length === legacy.length) {
            await RecordingStore.clear();
            reapedLegacy = legacy.length;
        } else {
            for (const entry of expired) {
                try { await RecordingStore.remove(entry.id); reapedLegacy++; } catch (e) { /* keep going */ }
            }
        }
    } catch (err) {
        logger.warn('SYSTEM', 'Legacy reaper failed', { error: err.message });
    }

    if (reapedSessions || reapedLegacy) {
        logger.info('SYSTEM', 'Reaped expired local recordings', {
            reapedSessions, reapedLegacy, retentionDays: LOCAL_PHI_RETENTION_DAYS,
        });
    }
}

// ============================================================================
// USER META CACHE
// ============================================================================

export function setUserMetaCache(meta) {
    try {
        const cache = {
            isAdmin: !!meta.isAdmin,
            analyticsEnabled: meta.analyticsEnabled !== undefined ? !!meta.analyticsEnabled : true,
            subscriptionStatus: meta.subscriptionStatus,
            trialEndDate: meta.trialEndDate,
            ts: Date.now(),
        };
        if (meta.visibleSections && typeof meta.visibleSections === 'object') {
            cache.visibleSections = {
                analytics: !!meta.visibleSections.analytics,
                admin: !!meta.visibleSections.admin,
                profiles: !!meta.visibleSections.profiles,
            };
        }
        localStorage.setItem(USER_META_KEY, JSON.stringify(cache));
    } catch (e) { /* storage disabled */ }
}

export function getUserMetaCache() {
    try {
        const raw = localStorage.getItem(USER_META_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (Date.now() - (parsed.ts || 0) > 7 * 24 * 60 * 60 * 1000) {
            localStorage.removeItem(USER_META_KEY);
            return null;
        }
        return parsed;
    } catch (e) { return null; }
}

// ============================================================================
// VISIBLE SECTIONS
// ============================================================================

export function updateAnalyticsMenuVisibility(analyticsEnabled) {
    const enabled = analyticsEnabled !== undefined ? !!analyticsEnabled : true;
    const shouldShow = enabled || state.isAdmin;
    const navAnalytics = document.getElementById('navAnalytics');
    if (navAnalytics) {
        navAnalytics.classList.toggle('hidden', !shouldShow);
    }
}

export function applyVisibleSections(sections) {
    if (!sections || typeof sections !== 'object') {
        sections = {
            analytics: true,
            admin: !!state.isAdmin,
            profiles: !!isProfileAdmin,
        };
    }
    var navAdmin = document.getElementById('navAdmin');
    var navAnalytics = document.getElementById('navAnalytics');
    var navProfile = document.getElementById('navProfile');
    var navManageProfiles = document.getElementById('navManageProfiles');
    var navFeatureFlags = document.getElementById('navFeatureFlags');

    if (navAdmin) navAdmin.classList.toggle('hidden', !sections.admin);
    if (navFeatureFlags) navFeatureFlags.classList.toggle('hidden', !state.isAdmin);
    if (navAnalytics) {
        var meta = getUserMetaCache();
        var analyticsFlag = meta ? meta.analyticsEnabled : true;
        var showAnalyticsMenu = sections.analytics && (analyticsFlag || state.isAdmin);
        navAnalytics.classList.toggle('hidden', !showAnalyticsMenu);
    }
    if (navManageProfiles) navManageProfiles.classList.toggle('hidden', !sections.profiles);
    if (navProfile) navProfile.classList.toggle('hidden', !currentProfileId);
}

export function getCachedVisibleSections() {
    var meta = getUserMetaCache();
    return (meta && meta.visibleSections) ? meta.visibleSections : null;
}

export function getCachedTrialState() {
    var meta = getUserMetaCache();
    return {
        status: (meta && meta.subscriptionStatus) || 'trial',
        trialEndDate: (meta && meta.trialEndDate) || null,
    };
}

// ============================================================================
// SESSION VERIFICATION & GOOGLE SIGN-IN
// ============================================================================

export async function verifySessionToken(token) {
    logger.info('AUTH', 'Verifying session token with backend');
    try {
        const res = await fetch(API_BASE + '/auth/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionToken: token }),
        });
        const data = await res.json();

        if (data.status === 'approved') {
            state.currentUser = data.user;
            state.isAdmin = data.isAdmin || false;
            state.idToken = token;
            setUserMetaCache({ isAdmin: state.isAdmin, visibleSections: data.visibleSections, subscriptionStatus: data.subscriptionStatus, trialEndDate: data.trialEndDate });
            applyVisibleSections(data.visibleSections || null);
            logger.info('AUTH', 'Session token verified — user approved', { isAdmin: state.isAdmin });
            showApp();
        } else {
            logger.warn('AUTH', 'Session token invalid/expired', { status: data.status });
            clearSessionToken();
            showLoginStatus('Session expired. Please sign in again.');
            showView('view-signin');
        }
    } catch (err) {
        logger.error('AUTH', 'Session token verification failed (network)', err, { online: navigator.onLine });
        if (state.currentUser) {
            logger.info('AUTH', 'Network error during session verify — keeping current session');
        } else {
            showLoginStatus(navigator.onLine ? 'Could not reach the server. Please try again.' : 'No internet connection.');
            showView('view-signin');
        }
    }
}

export function handleGoogleSignIn(response) {
    sendBeacon('auth_start');
    const googleToken = response.credential;
    verifyToken(googleToken);
}

export async function verifyToken(token) {
    logger.info('AUTH', 'Verifying token with backend');
    showLoginStatus('');
    try {
        const res = await fetch(API_BASE + '/auth/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
        });
        const data = await res.json();

        if (data.status === 'approved') {
            state.currentUser = data.user;
            state.isAdmin = data.isAdmin || false;
            if (data.sessionToken) {
                setSessionToken(data.sessionToken);
            } else {
                setSessionToken(token, { legacy: true });
            }
            setUserMetaCache({ isAdmin: state.isAdmin, visibleSections: data.visibleSections, subscriptionStatus: data.subscriptionStatus, trialEndDate: data.trialEndDate });
            applyVisibleSections(data.visibleSections || null);
            logger.info('AUTH', 'Token verified — user approved', { email: data.user?.email, isAdmin: state.isAdmin });
            showApp();
        } else if (data.status === 'pending') {
            state.currentUser = data.user;
            logger.info('AUTH', 'User status: pending approval');
            showView('view-pending');
        } else if (data.status === 'new') {
            state.currentUser = data.user;
            state.idToken = token;
            logger.info('AUTH', 'User status: new — showing waitlist form');
            showWaitlistForm(data.user);
        } else if (data.status === 'expired' || res.status === 401) {
            logger.warn('AUTH', 'Token expired/invalid', { httpStatus: res.status });
            clearSessionToken();
            showLoginStatus('Session expired. Please sign in again.');
            showView('view-signin');
        } else {
            logger.warn('AUTH', 'Unexpected auth status', { status: data.status, httpStatus: res.status });
            showLoginStatus('Login failed (unexpected response). Please try again.');
            showView('view-signin');
        }
    } catch (err) {
        logger.error('AUTH', 'Token verification failed', err, { hadUser: !!state.currentUser, online: navigator.onLine });
        if (state.currentUser) {
            logger.info('AUTH', 'Network error during verify — keeping current session');
        } else {
            showLoginStatus(navigator.onLine ? 'Login failed — server error. Please try again.' : 'No internet connection. Please check your network.');
            showView('view-signin');
        }
    }
}

export function signOut() {
    if (state.isRecording || state.isPaused) {
        logger.warn('AUTH', 'Sign-out blocked — recording active');
        return;
    }
    google.accounts.id.disableAutoSelect();
    clearSessionToken();
    clearLocalPhiOnSignOut(_deps.migrateLegacyRecordings);
    _deps.clearProfileSession();
    state.currentUser = null;
    state.isAdmin = false;
    profilesData = null;
    _deps.stopInactivityTracker();
    showView('view-signin');
}

// ============================================================================
// LOGIN STATUS
// ============================================================================

export function showLoginStatus(msg) {
    const el = document.getElementById('loginStatusMsg');
    if (!el) return;
    if (msg) {
        el.textContent = msg;
        el.classList.remove('d-none');
    } else {
        el.textContent = '';
        el.classList.add('d-none');
    }
}

// ============================================================================
// POST-AUTH NAVIGATION
// ============================================================================

export function showApp() {
    ProfileStore.restoreSession();
    if (ProfileStore.get('currentProfileId')) {
        _deps.proceedToApp();
        refreshProfilesBackground();
        return;
    }
    _deps.checkProfilesAndProceed();
}

export function showWaitlistForm(user) {
    showView('view-waitlist');
    document.getElementById('wl-name').value = user.name || '';
    document.getElementById('wl-email').value = user.email || '';
}

export async function submitWaitlist(event) {
    event.preventDefault();
    const btn = document.getElementById('wl-submit');
    const statusEl = document.getElementById('wl-status');
    btn.disabled = true;
    btn.textContent = 'Submitting...';
    statusEl.textContent = '';

    try {
        const res = await fetch(API_BASE + '/auth/waitlist', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                token: state.idToken,
                name: document.getElementById('wl-name').value,
                clinicName: document.getElementById('wl-clinic').value,
                phone: document.getElementById('wl-phone').value,
                role: document.getElementById('wl-role').value,
                referral: document.getElementById('wl-referral').value,
            }),
        });
        const data = await res.json();

        if (data.status === 'pending') {
            showView('view-pending');
        } else if (data.status === 'approved') {
            verifyToken(state.idToken);
        } else {
            statusEl.textContent = data.error || 'Something went wrong';
            statusEl.className = 'status-msg error';
        }
    } catch (err) {
        statusEl.textContent = 'Network error. Please try again.';
        statusEl.className = 'status-msg error';
    } finally {
        btn.disabled = false;
        btn.textContent = 'Submit Request';
    }
}

export function notifyKeptUnsentAudio() {
    let count = 0;
    try { count = parseInt(localStorage.getItem(UNSENT_KEPT_KEY) || '0', 10) || 0; } catch (e) { count = 0; }
    if (count <= 0) return;
    try { localStorage.removeItem(UNSENT_KEPT_KEY); } catch (e) { /* ignore */ }
    setStatus('recordStatus',
        count + ' recording' + (count > 1 ? 's' : '') + ' from a previous session ' +
        (count > 1 ? 'are' : 'is') + ' still waiting to upload — see Recent Recordings below.',
        '');
    logger.info('SYSTEM', 'Unsent recordings retained across sign-out', { count });
}

export async function refreshProfilesBackground() {
    try {
        const res = await apiFetch('/profiles');
        if (!res.ok) return;
        const data = await res.json();
        profilesData = data;

        if (data.profilesEnabled && data.profiles && data.profiles.length > 0) {
            if (currentProfileId) {
                const profileExists = data.profiles.some(p => p.profileId === currentProfileId);
                if (!profileExists) {
                    logger.warn('AUTH', 'Active profile no longer exists — showing picker');
                    _deps.clearProfileSession();
                    _deps.renderProfilePicker(data.profiles);
                    showView('view-profiles');
                }
            }
        }
    } catch (err) {
        logger.warn('AUTH', 'Background profiles refresh failed', { error: err.message });
    }
}

// ============================================================================
// DEPENDENCY INJECTION — wired by main.js after all modules load
// Avoids circular imports for functions that live in other modules.
// ============================================================================
export const _deps = {
    proceedToApp: () => {},
    checkProfilesAndProceed: () => {},
    clearProfileSession: () => {},
    stopInactivityTracker: () => {},
    migrateLegacyRecordings: async () => {},
    renderProfilePicker: () => {},
};
